import {useCallback, useRef, useState} from "react";
import type {CheckoutDocumentRead} from "pay/checkoutDocument";
import {MONTHLY} from "pricing/PricingStorage";
import {type InPageCheckoutOutcome, useEmbeddedSubscriptionCheckout} from "pay/useEmbeddedSubscriptionCheckout";
import type {CheckoutFailureMessageKey} from "pay/checkoutFailure";
import type {TrialHoldTerms} from "pay/trialHoldTerms";
import {useLoginModal} from "login/LoginModalProvider";
import {useAuthenticatedSession, useIsLoggedIn} from "UserProvider";
import {useAuthoritativeSessionWait} from "auth/useAuthoritativeSessionWait";
import {settledCreationAction} from "home/transferCreationAuth";
import {useSingleFlight} from "utils/useSingleFlight";
import {paymentAdmission, type PaymentAdmission} from "pay/withdrawalRight";
import {clearCheckoutHandoff, rememberCheckoutHandoff, takeCheckoutHandoff} from "./checkoutHandoff";
import {clientReportsCheckoutStep} from "offers/ladder/ladderOffer";
import {exitOfferPlanFor, type PricingExitOfferVariant} from "./exitOffer";
import {offerEvents, type OfferPrice} from "./offerEvents";

/** The exit offer's CTA, as the dialog consumes it. */
export interface ExitOfferPurchase {
	/**
	 * Sign the reader in if they are not, then start the offer's checkout. `price` is the figure the
	 * dialog showed for `variant` (null on the free trial), recorded on the funnel's checkout step.
	 * `opener` is the pressed CTA, where focus returns when a sign-in this press opens closes: a press
	 * held for the restored session is disabled while it waits and loses focus (ui#3117).
	 */
	continueWithOffer: (variant: PricingExitOfferVariant, price: OfferPrice | null, opener?: HTMLElement) => Promise<void>;
	/**
	 * Whether the reader has expressly agreed to the subscription starting at once
	 * (kitecorp/transferify-ui#1844). Starts FALSE on every open and is never seeded true.
	 */
	acknowledged: boolean;
	/** The reader's own act, in both directions. */
	setAcknowledged: (next: boolean) => void;
	/**
	 * Whether either CTA may run yet. `"acknowledgementRequired"` until they have agreed, which is what
	 * the dialog disables its button on rather than testing the boolean itself.
	 */
	admission: PaymentAdmission;
	/**
	 * True once the api has refused the offer. The dialog must say so and let the reader decide again;
	 * it is not an error state and nothing was charged.
	 */
	offerUnavailable: boolean;
	/**
	 * True once the api has refused the LIFECYCLE LADDER TOKEN this purchase carried
	 * (kitecorp/transferify-ui#1977), which is a different fact from {@link offerUnavailable}.
	 *
	 * That one means the exit offer's terms went away and the plan is still for sale at its regular
	 * price here. This one means the LINK went away: the 72 hours ran out, or the signed-in account is
	 * not the one the message was addressed to, which a reader meets on their very first press. Nothing
	 * was charged either way, and the surface that carried the token is the only place that can say so.
	 */
	offerExpired: boolean;
	/**
	 * The deliberate second press: buy the plan the refused `variant` sells at its regular monthly price,
	 * `price`. The variant is named rather than remembered so the plan the notice priced is the plan started.
	 */
	continueAtRegularPrice: (variant: PricingExitOfferVariant, price: OfferPrice | null) => Promise<void>;
	/**
	 * True from the press until the checkout session exists: while the press waits for the restored
	 * sign-in to answer (kitecorp/transferify-ui#3117) and while the session is being created.
	 */
	redirecting: boolean;
	/**
	 * The term the open checkout session was minted for, or null before any. The payment form states
	 * THIS term: the rows stay live while a press is held for the restored session (ui#3117) and while
	 * the session is minted, so the row selected when the form appears may not be the one being paid for.
	 */
	checkoutVariant: PricingExitOfferVariant | null;
	clientSecret: string | null;
	/** Whether the minted Session collects a shipping address, so the form mounts the element for it (api#2390). */
	collectsShippingAddress: boolean;
	/**
	 * The card hold of the Session {@link clientSecret} mounts, when it is the free trial's
	 * (kitecorp/transferify-api-service#2265): the order form states it above the order button.
	 */
	trialHold: TrialHoldTerms | null;
	error: CheckoutFailureMessageKey | null;
	/**
	 * The surface closed or the reader backed out of the payment: unmounts the payment sheet, forgets the
	 * last failure, and abandons a press still waiting for the restored session (ui#3117). The exit
	 * offer's dialog stays mounted when it closes, so without this a held press would carry on for an
	 * offer nobody is looking at.
	 */
	dismiss: () => void;
	completed: () => void;
}

/**
 * "Continue with offer", as a thin adapter over rules that already exist
 * (kitecorp/transferify-ui#1722).
 *
 * Nothing here is new policy. The admission rule is {@link settledCreationAction} — the same
 * React-free verdict the create flow and the public reminder upload use, reused rather than restated
 * so a visitor meets one sign-in rule across the whole app. A reader the page shows signed in whose
 * restored session has not answered yet is HELD, not sent to the login modal (owner decision
 * 2026-09-29, kitecorp/transferify-ui#3117): the CTA shows busy until the session answers, then
 * continues, or asks for a sign-in only if the session settled signed out. The checkout, its failure
 * handling and the offer's own fallback to full price all live in {@link useStartCheckout} and the
 * seam beneath it.
 *
 * <b>MONTHLY is passed explicitly and is not a default.</b> `useStartCheckout` takes the period as a
 * REQUIRED argument (ui#2390) and defaults to nothing at all, which is what this offer needs: the api
 * refuses `offerVariant` on any price that is not the term's own tier monthly (Premium, or Plus for the
 * first-month term), so the one period that can carry this discount is named here rather than inherited. The same reasoning the first-month offer's dialog
 * gives at its own CTA.
 *
 * <b>A successful sign-in proceeds straight to checkout rather than re-running the verdict.</b> The
 * login modal resolving `true` IS the sign-in having succeeded; re-asking through the render closure's
 * `isLoggedIn` would read the visitor who existed BEFORE the modal, and a stale "still anonymous"
 * answer would make the button look dead. If the session genuinely is not usable yet the api answers
 * 401, which `useStartCheckout` already turns into the localized session-expired toast plus a fresh
 * login — a visible recovery rather than a silent no-op.
 */
/**
 * @param offerToken the lifecycle offer ladder token this purchase came from
 *                   (kitecorp/transferify-ui#1977), or undefined for the ordinary exit offer. It rides
 *                   BESIDE the variant rather than instead of it: the variant says what the checkout
 *                   is, and the token says which mailed rung it belongs to, so a ladder reader's
 *                   day-14 sale is attributed and an ordinary one is unchanged.
 */
/*
 * `readSelection` is a getter rather than the reader itself because `continueWithOffer` below can await
 * a sign-in modal before it posts. A captured reader would be the anonymous one, and submitting it
 * after the account changed throws. Read it after every await instead, never before one.
 */
export function useExitOfferPurchase(
	offerToken?: string, readSelection?: () => CheckoutDocumentRead | undefined,
): ExitOfferPurchase {
	const inPage = useEmbeddedSubscriptionCheckout();
	const {start, busy: minting} = inPage;
	const isLoggedIn = useIsLoggedIn();
	const [authenticatedSession] = useAuthenticatedSession();
	const {open: openLogin} = useLoginModal();
	// A press by a reader shown signed in, before Firebase has answered, waits here (ui#3117).
	const sessionWait = useAuthoritativeSessionWait(authenticatedSession);
	// One CTA, one press at a time: a second press before the busy render lands joins the first instead
	// of reporting a second checkout step behind the same wait.
	const cta = useSingleFlight();

	const [offerUnavailable, setOfferUnavailable] = useState(false);
	const [offerExpired, setOfferExpired] = useState(false);
	const [checkoutVariant, setCheckoutVariant] = useState<PricingExitOfferVariant | null>(null);
	/**
	 * FALSE on every mount, and there is no path that seeds it true (kitecorp/transferify-ui#1844). A
	 * pre-ticked box is not consent under art. 16 lit. a) OUG 34/2014, so a "remember their last answer"
	 * convenience here would void the acknowledgement while leaving the surface looking compliant.
	 */
	const [acknowledged, setAcknowledgedState] = useState(false);
	/*
	 * The acknowledgement as it stands NOW, for a press that has awaited something since it was made: a
	 * press held for the restored session (ui#3117) resumes from the closure of the render it was pressed
	 * in, which still holds the answer the reader may since have taken back.
	 */
	const acknowledgedNow = useRef(false);
	const setAcknowledged = useCallback((next: boolean) => {
		acknowledgedNow.current = next;
		setAcknowledgedState(next);
	}, []);

	/**
	 * The funnel's checkout step (kitecorp/transferify-ui#1965), reported at the moment the redirect is
	 * asked for — after the acknowledgement and after the sign-in, so a press that opened the login modal
	 * and went no further is not a checkout. The handoff is what lets the activation page report the
	 * payment this tab comes back with, and it is withdrawn again when the api refuses or fails the
	 * session: a reader who never left for Stripe has nothing to be attributed on their return.
	 */
	const checkoutStarted = useCallback(async (
		price: OfferPrice | null, begin: () => Promise<InPageCheckoutOutcome>,
	): Promise<InPageCheckoutOutcome> => {
		// WHO WITNESSES THIS STEP is a rule, not a preference (kitecorp/transferify-ui#1977): a checkout
		// carrying a ladder token belongs to the ladder's own emailed surface, which only the server may
		// report on. Attributing it to this dialog's moment would count a reader who never opened the
		// pricing dialog into the funnel that measures it, and the handoff would do the same on the way
		// back from Stripe.
		if (!clientReportsCheckoutStep(offerToken)) {
			return begin();
		}
		offerEvents.checkoutOpened("EXIT_OFFER", price);
		rememberCheckoutHandoff({moment: "EXIT_OFFER", price, occasion: null});
		const outcome = await begin();
		if (outcome !== "ready") {
			clearCheckoutHandoff("EXIT_OFFER");
		}
		return outcome;
	}, [offerToken]);

	const continueWithOffer = useCallback((
		variant: PricingExitOfferVariant, price: OfferPrice | null, opener?: HTMLElement,
	): Promise<void> => cta.run(async () => {
		// The gate, before the sign-in and before anything is minted: a reader who has not agreed has not
		// started a contract, so there is nothing to sign them in FOR. The dialog also disables its CTA on
		// the same verdict; this is the half that survives a view being restyled around it.
		if (paymentAdmission(acknowledgedNow.current) === "acknowledgementRequired") {
			return;
		}
		const action = await settledCreationAction(isLoggedIn, authenticatedSession, sessionWait.wait);
		if (action === "waitCancelled") {
			// The dialog went away while the press waited for the session: there is nobody left to buy for.
			return;
		}
		// A dismissed modal resolves false and buys nothing — the reader closed the sign-in, which is an
		// answer, not a failure.
		if (action === "requestSignIn" && !await openLogin(opener)) {
			return;
		}
		// Asked again once the session has answered: the box stays live while the press is held, and a
		// reader who took their agreement back meanwhile has agreed to nothing (ui#1844).
		if (paymentAdmission(acknowledgedNow.current) === "acknowledgementRequired") {
			return;
		}
		// The refusal is a fact to state, not an error to swallow (owner decision 2026-08-31): the
		// dialog switches to the regular price and waits for a second, deliberate press rather than
		// redirecting a reader who agreed to 0.99 EUR onto a full-price page.
		// AFTER the sign-in above, deliberately: see the getter's note on the parameter.
		const documentSelection = readSelection?.();
		setCheckoutVariant(variant);
		const outcome = await checkoutStarted(price, () => start(exitOfferPlanFor(variant), MONTHLY,
			{uiMode: "elements", offerVariant: variant, immediatePerformanceConsent: acknowledgedNow.current, offerToken, ...(documentSelection ? {documentSelection} : {})}));
		if (outcome === "offerUnavailable") {
			setOfferUnavailable(true);
		}
		if (outcome === "ladderOfferExpired") {
			setOfferExpired(true);
		}
	}), [cta, isLoggedIn, authenticatedSession, sessionWait.wait, openLogin, start, checkoutStarted, offerToken, readSelection]);

	// No variant, so nothing can refuse it a second time for the same reason. The reader is already
	// signed in by the time this is reachable: it only appears after an attempt that got far enough to
	// be refused by the api.
	//
	// THE ACKNOWLEDGEMENT CARRIES OVER FROM THE FIRST PRESS, and is re-sent with this request. It is not
	// re-asked, and that is a decision rather than an oversight: the sentence the reader ticked is about
	// PERFORMANCE BEGINNING IMMEDIATELY and what an early withdrawal then costs them. It says nothing
	// about the price, and the refusal changes only the price. Their agreement to start at once is as
	// true at the full monthly price as it was at 0.99, so re-asking would add a second tick that establishes nothing the
	// first one did not, on a surface where the reader has just been told their terms went away.
	//
	// What is NOT reused is the press. `offerUnavailable` swaps the CTA's label, and the reader has to
	// press again deliberately, having read the new figure (owner decision 2026-08-31). That deliberate
	// second press is what makes this a fresh purchase decision; the acknowledgement is the same
	// statement about the same thing.
	//
	// If counsel prefers the acknowledgement to be re-asked at the new price, the change is one line —
	// reset it beside `setOfferUnavailable(true)` above — and the dialog re-disables its CTA for free.
	//
	// It shares the offer press's single flight because it is the same CTA with a new label.
	const continueAtRegularPrice = useCallback((
		variant: PricingExitOfferVariant, price: OfferPrice | null,
	): Promise<void> => cta.run(async () => {
		if (paymentAdmission(acknowledgedNow.current) === "acknowledgementRequired") {
			return;
		}
		// THE LADDER TOKEN IS KEPT on this press (kitecorp/transferify-ui#1977). This path is reached only
		// from `offerUnavailable`, which is `error.pay.exitOfferNotEligible`: the VARIANT was refused and
		// the link is untouched. Dropping the token here would lose the sale's attribution twice over —
		// the api counts no `checkout_opened` or `paid` on `email_ladder_d14` without it, and this client
		// reports nothing on that surface either, so the reader would be invisible in the one ratio the
		// ladder is judged by. A token that really is dead answers `ladderOfferExpired` below instead.
		const documentSelection = readSelection?.();
		setCheckoutVariant(variant);
		const outcome = await checkoutStarted(price, () => start(exitOfferPlanFor(variant), MONTHLY,
			{uiMode: "elements", immediatePerformanceConsent: acknowledgedNow.current, offerToken, ...(documentSelection ? {documentSelection} : {})}));
		if (outcome === "ladderOfferExpired") {
			setOfferExpired(true);
		}
	}), [cta, start, checkoutStarted, offerToken, readSelection]);

	const {dismiss: dismissSheet} = inPage;
	const cancelHeldPress = sessionWait.cancel;
	const dismiss = useCallback(() => {
		cancelHeldPress();
		dismissSheet();
	}, [cancelHeldPress, dismissSheet]);

	return {
		continueWithOffer, offerUnavailable, offerExpired, continueAtRegularPrice,
		redirecting: minting || sessionWait.waiting, checkoutVariant,
		clientSecret: inPage.clientSecret, collectsShippingAddress: inPage.collectsShippingAddress,
		trialHold: inPage.trialHold, error: inPage.error, dismiss,
		completed: () => {
			const handoff = takeCheckoutHandoff();
			if (handoff?.moment === "EXIT_OFFER") offerEvents.paid(handoff.moment, handoff.price, handoff.occasion);
			inPage.completed();
		},
		acknowledged, setAcknowledged, admission: paymentAdmission(acknowledged),
	};
}
